import React from "react";

import * as orm from "@client/features/orm";

import * as router from "@client/router";
import * as typography from "@client/shared/typography";

import * as forms from "@client/site/forms";
import {server} from "@reactivated";

const styles = {
    planner: "space-y-2.5",
    spacer: "space-y-2.5",
    grid: "planner-grid gap-2.5 flex flex-wrap",
} as const;

const DAYS_OF_WEEK_BY_INDEX = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

type TargetState = {
    id: number | null;
    name: string;
    calories_target: string | null;
    protein_target: string | null;
    fat_target: string | null;
    carbs_target: string | null;
    net_carbs_target: string | null;
};

const TARGET_DEFAULTS: TargetState = {
    id: null,
    name: "",
    calories_target: null,
    protein_target: null,
    fat_target: null,
    carbs_target: null,
    net_carbs_target: null,
};

const targetFormSchema = server.journal.day.TargetInput;

const TargetFormFields = ({
    target,
    targetIndex,
    setTargets,
}: {
    target: TargetState;
    targetIndex: number;
    setTargets: React.Dispatch<React.SetStateAction<TargetState[]>>;
}) => {
    const form = forms.usePform(
        targetFormSchema.fields,
        {
            name: target.name,
            calories_target: target.calories_target,
            protein_target: target.protein_target,
            fat_target: target.fat_target,
            carbs_target: target.carbs_target,
            net_carbs_target: target.net_carbs_target,
        },
        (values) => {
            setTargets((prev) =>
                prev.map((t, i) => (i === targetIndex ? {...t, ...values} : t)),
            );
        },
    );

    return (
        <>
            {targetFormSchema.iterator.map((fieldName) => (
                <forms.PField key={fieldName} field={form.fields[fieldName]} />
            ))}
        </>
    );
};

export const Planner = router.Planner.withHooks(({profile}) => {
    const initialTargets: TargetState[] = profile.user.targets.map((t) => ({
        id: t.id,
        name: t.name,
        calories_target: t.calories_target,
        protein_target: t.protein_target,
        fat_target: t.fat_target,
        carbs_target: t.carbs_target,
        net_carbs_target: t.net_carbs_target,
    }));

    const initialAssignments = [...Array(7).keys()].map((dayOfWeek) => {
        const referenceIndex = initialTargets.findIndex(
            (t) => t.id === profile.assignments[dayOfWeek].target_id,
        );
        return {
            reference: referenceIndex === -1 ? "" : referenceIndex.toString(),
        };
    });

    const [targets, setTargets] = React.useState(initialTargets);
    const [assignments, setAssignments] = React.useState(initialAssignments);

    return {
        targets,
        setTargets,
        assignments,
        setAssignments,
    };
})
    .withIndex((props) => {
        const references: readonly (readonly [string, string])[] = [
            ["", "No target"] as const,
            ...props.targets.map(
                (target, index) =>
                    [
                        index.toString(),
                        target.name === "" ? `<Target ${index}>` : target.name,
                    ] as const,
            ),
        ];

        return (
            <div className={styles.planner}>
                <typography.Title>Targets</typography.Title>
                {props.targets.length > 0 ? (
                    <div className={styles.grid}>
                        {props.targets.map((target, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                }}
                            >
                                <TargetFormFields
                                    target={target}
                                    targetIndex={index}
                                    setTargets={props.setTargets}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <typography.P>
                        Add a target and assign it to days of the week to get started.
                    </typography.P>
                )}

                <typography.Title>Schedule</typography.Title>
                <div className={styles.grid}>
                    {props.assignments.map((assignment, index) => (
                        <div key={index}>
                            <forms.PField
                                field={{
                                    type: "select",
                                    name: `assignment_${index}`,
                                    label: DAYS_OF_WEEK_BY_INDEX[index],
                                    required: true,
                                    options: references,
                                    widget: "select",
                                    value: assignment.reference,
                                    errors: null,
                                    onChange: (value: string) => {
                                        props.setAssignments((prev) =>
                                            prev.map((a, i) =>
                                                i === index
                                                    ? {...a, reference: value}
                                                    : a,
                                            ),
                                        );
                                    },
                                }}
                            />
                        </div>
                    ))}
                    <div /> {/* I am a fake item to make the flex grid have 8 items */}
                </div>
            </div>
        );
    })
    .withActions((props) => [
        {
            icon: "calculator",
            title: "Calculator",
            highlight: props.targets.length === 0,
            link: router.reverse("calculator.index", {}),
        },
        {
            icon: "eraser",
            title: "Clear",
            onClick: async () => {
                const result = await server.journal.day.target_planner({
                    targets: [],
                    assignments: props.assignments.map(() => ({
                        reference: null as number | null,
                    })),
                });

                if (result.type === "success") {
                    props.setTargets([]);
                    props.setAssignments((prev) =>
                        prev.map((a) => ({...a, reference: ""})),
                    );
                    props.notify("Targets and schedule cleared");
                    void props.dispatch(orm.fetchProfile());
                }
            },
        },
        {
            icon: "plus",
            title: "Add Target",
            highlight: props.targets.length === 0,
            onClick: () => {
                props.setTargets((prev) => [...prev, TARGET_DEFAULTS]);
            },
        },
        {
            icon: "save",
            title: "Save",
            onClick: async () => {
                const result = await server.journal.day.target_planner({
                    targets: props.targets,
                    assignments: props.assignments.map((a) => ({
                        reference:
                            a.reference === "" ? null : parseInt(a.reference, 10),
                    })),
                });

                if (result.type === "success") {
                    void props.analytics.track("SAVED_TARGET_ASSIGNMENTS");
                    await props.dispatch(orm.fetchProfile());
                    props.notify("Targets and schedule saved");
                }
            },
        },
    ])
    .withOptions(() => ({title: "Planner"}));
